import { definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";

import { SerializedComponent } from "../../components/SerializedApp";
import { ArrayScreenFormat, PropertyDescription, TableGlideType } from "../description";
import {
    FallbackInitialsSummaryArrayContentDescription,
    FallbackInitialsSummaryArrayScreenHandler,
    FallbackInitialsSummaryArrayScreenDescription,
} from "./summary-array-screen";
import { ListItemFlags } from "./list-item-flags";
import {
    PropertyDescriptor,
    PropertyTableGetter,
    ComponentBuilder,
    WireTableComponentHydrator,
    WireInflationBackend,
} from "../components/index";
import { getColumnProperty, getEnumProperty } from "../components/property-utils";
import { makeCaptionPropertyDescriptor } from "../components/descriptor-utils";
import { handleTruncationAndGrouping } from "./truncation-lib";
import { makeEmptyMessage, InlineListBuild } from "./array-content";
import { getPropertyWithColumnFormat } from "../formulas";
import { handleActionIcon } from "../builder-utils";
import { EnumPropertyHandler, SwitchPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { ListItemSize } from "../components/list-types";
import { asString } from "../../computation-model/data";
import { registerActionRunner, inflateActions } from "../../wire/utils";
import { WireComponentKind } from "../../wire/component-kind";

const allowWrappingPropertyHandler = new SwitchPropertyHandler(
    { allowWrapping: false },
    "Allow text wrapping",
    PropertySection.TextStyle
);

const itemSizePropertyHandler = new EnumPropertyHandler(
    { itemSize: ListItemSize.Regular },
    "Item Size",
    "Item Size",
    [
        {
            value: ListItemSize.Regular,
            label: "Regular",
            icon: "listOrientationVertical",
        },
        {
            value: ListItemSize.Compact,
            label: "Compact",
            icon: "itemSizeCompact",
        },
    ],
    PropertySection.Design,
    "small-images"
);

export interface ListArrayContentDescription extends FallbackInitialsSummaryArrayContentDescription {
    readonly captionProperty?: PropertyDescription;
    readonly itemSize?: PropertyDescription;
}

export interface ListArrayScreenDescription
    extends FallbackInitialsSummaryArrayScreenDescription,
        ListArrayContentDescription {}

function getItemSize(desc: ListArrayContentDescription | undefined): ListItemSize {
    return getEnumProperty(desc?.itemSize) ?? ListItemSize.Regular;
}

export class ListArrayScreenHandler extends FallbackInitialsSummaryArrayScreenHandler<
    ListArrayContentDescription,
    ListArrayScreenDescription
> {
    protected readonly supportsNonURLImages = false;
    protected readonly supportsEmojiImages = true;
    protected readonly supportsTruncateList = true;

    constructor() {
        super(ArrayScreenFormat.List, "List", "list", false);
    }

    protected get supportsGrouping(): boolean {
        return true;
    }

    protected getInterSummaryPropertyDescriptors<T extends ListArrayContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        _insideInlineList: boolean,
        desc: T | undefined
    ): readonly PropertyDescriptor[] {
        if (getItemSize(desc) === ListItemSize.Regular) {
            return [makeCaptionPropertyDescriptor(getPropertyTable, true)];
        } else {
            return [];
        }
    }

    public getBasicSearchProperties(desc: ListArrayContentDescription): readonly string[] {
        const props = super.getBasicSearchProperties(desc);
        const captionColumn = getColumnProperty(desc.captionProperty);
        if (captionColumn === undefined) {
            return props;
        } else {
            return [...props, captionColumn];
        }
    }

    protected contentWithFallbackInitials(
        builder: ComponentBuilder,
        desc: ListArrayContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        initials: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        caption: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        const itemSize = getItemSize(desc);

        const itemCaption = definedMap(getColumnProperty(desc.captionProperty), n =>
            getPropertyWithColumnFormat(builder, table, n)
        );
        const allowWrapping = allowWrappingPropertyHandler.getSwitch(desc);
        const emptyMessage = makeEmptyMessage(b, searchActiveFlag, inlineListBuild);

        let flags: ListItemFlags = ListItemFlags.DrawSeparator | ListItemFlags.DisableLongPress;
        if (itemSize === ListItemSize.Compact) {
            flags |= ListItemFlags.SmallImage | ListItemFlags.HorizontalLayout;
        }
        if (allowWrapping) {
            flags |= ListItemFlags.WrapText;
        }

        let icon: SerializedComponent | undefined;
        if (itemSize === ListItemSize.Compact || itemCaption === undefined) {
            const iconAndFlags = this.makeIconAndFlagsForDefaultAction(b, desc, flags);
            flags = iconAndFlags.flags;
            icon = iconAndFlags.icon;
        } else {
            flags |= ListItemFlags.SwapImageSide + ListItemFlags.LargeImage + ListItemFlags.DisableChevron;
        }

        return handleTruncationAndGrouping(
            builder,
            desc,
            table,
            arrayData,
            inlineListBuild,
            caption,
            1,
            true,
            (listData, isGrouped, a) => {
                if (getItemSize(desc) === ListItemSize.Regular) {
                    return b.ui("List", {
                        list: listData,
                        caption,
                        emptyMessage,
                        isGrouped,
                        allowWrapping: b.constant(allowWrapping),
                        item: handleActionIcon(b, onTap, icon, true, (ot, ic) =>
                            b.adaptiveListItem(
                                title,
                                subtitle,
                                itemCaption,
                                image,
                                ic,
                                initials,
                                ot,
                                onDropImage,
                                undefined,
                                b.constant(flags),
                                undefined,
                                undefined,
                                itemID
                            )
                        ),
                        ...a,
                    });
                } else {
                    return b.ui("SmallList", {
                        list: listData,
                        caption,
                        emptyMessage,
                        isGrouped,
                        allowWrapping: b.constant(allowWrapping),
                        item: handleActionIcon(b, onTap, icon, true, (ot, ic) =>
                            b.adaptiveListItem(
                                title,
                                subtitle,
                                undefined,
                                image,
                                ic,
                                initials,
                                ot,
                                onDropImage,
                                undefined,
                                b.constant(flags),
                                undefined,
                                undefined,
                                itemID
                            )
                        ),
                        ...a,
                    });
                }
            }
        );
    }

    public getAdditionalPropertyDescriptors<T extends ListArrayContentDescription>(
        _getPropertyTable: PropertyTableGetter<T> | undefined,
        _insideInlineList: boolean,
        _desc: T
    ): ReadonlyArray<PropertyDescriptor> {
        return [allowWrappingPropertyHandler, itemSizePropertyHandler];
    }

    public inflateContent<T extends ListArrayContentDescription>(
        ib: WireInflationBackend,
        desc: T
    ): WireTableComponentHydrator | undefined {
        const { actions } = this.getActions(desc, ib);
        const hydrator = inflateActions(ib, actions);

        const [titleGetter] = ib.getValueGetterForProperty(desc.titleProperty, true);
        const [subtitleGetter] = ib.getValueGetterForProperty(desc.subtitleProperty, true);
        const [captionGetter] = ib.getValueGetterForProperty(desc.captionProperty, true);
        // FIXME: support arrays
        const [imageURLGetter] = ib.getValueGetterForProperty(desc.imageURLProperty, true);

        return hb => {
            const data = hb.tableScreenContext;
            if (data.length === 0) return undefined;

            return {
                kind: WireComponentKind.List,
                format: ArrayScreenFormat.List,
                componentTitle: "",
                items: mapFilterUndefined(data, row => {
                    const rhb = hb.makeHydrationBackendForRow(row);

                    const title = asString(titleGetter(rhb));
                    const subtitle = asString(subtitleGetter(rhb));
                    const caption = asString(captionGetter(rhb));
                    // FIXME: support arrays
                    const imageURL = asString(imageURLGetter(rhb));

                    const runner = hydrator?.(rhb);
                    const action = definedMap(runner, r => registerActionRunner(rhb, r));

                    return {
                        title,
                        subtitle,
                        caption,
                        imageURL,
                        action,
                    };
                }),
            };
        };
    }
}
